import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode';
import BiosenseSignalMonitor from './BiosenseSignalMonitor';
import SettingsBars from './SettingsBars';
import AppLoader from './AppLoader';
import { Flex } from './shared/Flex';
import { theme } from '../style/theme';
import { useCameras, useDisableZoom, useLicenseKey, useSDKInitialization } from '../hooks';
import { isMobileDevice } from '../utils/deviceDetection';

const Container = styled(Flex)<{ isSettingsOpen: boolean }>`
  height: 100%;
  width: 100%;
  position: relative;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  background: ${({ isSettingsOpen }) =>
    isSettingsOpen 
      ? 'rgba(0, 0, 0, 0.6)' 
      : `${theme.colors.neutral.gray50}`};
  transition: background ${theme.transitions.normal};
  backdrop-filter: ${({ isSettingsOpen }) => isSettingsOpen && 'blur(4px)'};
`;

const App = () => {
  const cameras = useCameras();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [cameraId, setCameraId] = useState<string>();
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [isMobile, setIsMobile] = useState(true); // Default to true to avoid flash
  const [currentUrl, setCurrentUrl] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [licenseKey] = useLicenseKey();
  const { isInitializing, isReady, progress, message } = useSDKInitialization(licenseKey);
  useDisableZoom();

  // Generate QR code using client-side library
  const generateQRCode = async (text: string) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(text, {
        width: 250,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      return qrDataUrl;
    } catch (err) {
      console.error('Failed to generate QR code:', err);
      return '';
    }
  };

  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);
    
    if (!mobile) {
      const url = window.location.href;
      setCurrentUrl(url);
      generateQRCode(url).then(qrUrl => {
        setQrCodeUrl(qrUrl);
      });
    }
  }, []);

  const onSettingsClickedHandler = useCallback((event) => {
    const settingsBars = document.getElementById('settingsBars');
    const isSettingsButtonClicked = event.target.id === 'settingsButton';

    const isInsideSettingsClicked =
      settingsBars.contains(event.target as Node) || isSettingsButtonClicked;

    if (!isInsideSettingsClicked) {
      setIsSettingsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', onSettingsClickedHandler);
    return () => {
      document.removeEventListener('click', onSettingsClickedHandler);
    };
  }, []);

  const updateLicenseStatus = useCallback((valid) => {
    setIsLicenseValid(valid);
  }, []);

  const toggleSettingsClick = useCallback(() => {
    setIsSettingsOpen(!isSettingsOpen);
  }, [isSettingsOpen]);

  const handleCloseSettings = useCallback(({ cameraId }) => {
    setCameraId(cameraId);
    setIsSettingsOpen(false);
  }, []);

  useEffect(() => {
    if (!cameras?.length) return;
    setCameraId(cameras[0].deviceId);
  }, [cameras]);

  // Show loading screen while SDK is initializing
  if (isInitializing) {
    return <AppLoader progress={progress} message={message} />;
  }

  // Only render the scanner UI if on mobile
  if (!isMobile) {
    const QRWrapper = styled.div`
      min-height: 100vh;
      background: linear-gradient(135deg, ${theme.colors.neutral.gray50} 0%, ${theme.colors.neutral.gray100} 100%);
      padding: ${theme.spacing[12]} ${theme.spacing[4]};
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    const QRContainer = styled.div`
      max-width: 1000px;
      width: 100%;
    `;

    const QRHeader = styled.div`
      text-align: center;
      margin-bottom: ${theme.spacing[12]};
    `;

    const QRTitle = styled.h1`
      font-size: ${theme.typography.fontSize['4xl']};
      font-weight: ${theme.typography.fontWeight.bold};
      color: ${theme.colors.neutral.gray900};
      margin-bottom: ${theme.spacing[4]};
    `;

    const QRSubtitle = styled.p`
      font-size: ${theme.typography.fontSize.xl};
      color: ${theme.colors.neutral.gray600};
      max-width: 600px;
      margin: 0 auto;
      line-height: ${theme.typography.lineHeight.relaxed};
    `;

    const QRCard = styled.div`
      background: ${theme.colors.neutral.white};
      border-radius: ${theme.borderRadius['2xl']};
      box-shadow: ${theme.shadows.xl};
      padding: ${theme.spacing[12]} ${theme.spacing[8]};
      border: 1px solid ${theme.colors.neutral.gray200};
    `;

    const QRContent = styled.div`
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: ${theme.spacing[8]};
      align-items: center;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
        text-align: center;
      }
    `;

    const QRInstructions = styled.div`
      space-y: ${theme.spacing[6]};
    `;

    const QRInstructionsTitle = styled.h3`
      font-weight: ${theme.typography.fontWeight.semibold};
      color: ${theme.colors.primary.main};
      margin-bottom: ${theme.spacing[2]};
      font-size: ${theme.typography.fontSize.lg};
    `;

    const QRInstructionsList = styled.ol`
      color: ${theme.colors.neutral.gray700};
      font-size: ${theme.typography.fontSize.sm};
      line-height: ${theme.typography.lineHeight.relaxed};
      list-style: decimal;
      padding-left: ${theme.spacing[5]};
      margin: 0;

      li {
        margin-bottom: ${theme.spacing[1]};
      }
    `;

    const QRInstructionsBox = styled.div`
      background: ${theme.colors.primary.xlight};
      border-radius: ${theme.borderRadius.lg};
      padding: ${theme.spacing[4]};
      margin-top: ${theme.spacing[6]};
    `;

    const QRCodeSection = styled.div`
      text-align: center;
    `;

    const QRCodeContainer = styled.div`
      background: ${theme.colors.neutral.white};
      padding: ${theme.spacing[6]};
      border-radius: ${theme.borderRadius.xl};
      border: 2px solid ${theme.colors.primary.light};
      display: inline-block;
      margin-bottom: ${theme.spacing[4]};
    `;

    const QRCodeImage = styled.img`
      width: 250px;
      height: 250px;
      border-radius: ${theme.borderRadius.md};
    `;

    const QRCodeCaption = styled.p`
      font-size: ${theme.typography.fontSize.sm};
      color: ${theme.colors.neutral.gray500};
      margin: 0;
    `;

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(currentUrl);
        // You could add a toast notification here
      } catch (err) {
        console.error('Failed to copy URL');
      }
    };

    return (
      <QRWrapper>
        <QRContainer>
          <QRHeader>
            <QRTitle>BioSense Scanner QR Code</QRTitle>
            <QRSubtitle>
              Scan this QR code with your mobile device to access the BioSense Scanner
            </QRSubtitle>
          </QRHeader>

          <QRCard>
            <QRContent>
              <QRInstructions>
                <QRInstructionsBox>
                  <QRInstructionsTitle>Access BioSense Scanner:</QRInstructionsTitle>
                  <QRInstructionsList>
                    <li>Open your mobile device's camera app</li>
                    <li>Point the camera at the QR code</li>
                    <li>Tap the notification to open BioSense Scanner</li>
                    <li>The scanner will load directly on your mobile device</li>
                  </QRInstructionsList>
                </QRInstructionsBox>
              </QRInstructions>

              <QRCodeSection>
                <QRCodeContainer>
                  {qrCodeUrl ? (
                    <QRCodeImage 
                      src={qrCodeUrl}
                      alt="QR Code to access BioSense Scanner"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUwIiBoZWlnaHQ9IjI1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NzM4NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm9yIGdlbmVyYXRpbmcgUVIgY29kZTwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '250px',
                      height: '250px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: theme.colors.neutral.gray100,
                      borderRadius: theme.borderRadius.md,
                      color: theme.colors.neutral.gray500,
                      fontSize: theme.typography.fontSize.sm,
                      textAlign: 'center'
                    }}>
                      Generating QR Code...
                    </div>
                  )}
                </QRCodeContainer>
                <QRCodeCaption>
                  Scan to access BioSense Scanner on your mobile device
                </QRCodeCaption>
              </QRCodeSection>
            </QRContent>

            <div style={{ 
              marginTop: theme.spacing[8], 
              paddingTop: theme.spacing[6], 
              borderTop: `1px solid ${theme.colors.neutral.gray200}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: theme.spacing[4]
            }}>
              <div>
                <span style={{ 
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.neutral.gray700 
                }}>
                  Current URL:{' '}
                </span>
                <span style={{ 
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.primary.main,
                  wordBreak: 'break-all'
                }}>
                  {currentUrl}
                </span>
              </div>
              <button
                onClick={copyToClipboard}
                style={{
                  padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  borderRadius: theme.borderRadius.lg,
                  border: 'none',
                  cursor: 'pointer',
                  transition: `all ${theme.transitions.normal}`,
                  background: theme.colors.primary.main,
                  color: theme.colors.neutral.white
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = theme.colors.primary.dark;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = theme.colors.primary.main;
                }}
              >
                Copy URL
              </button>
            </div>
          </QRCard>
        </QRContainer>
      </QRWrapper>
    );
  }

  return (
    <Container isSettingsOpen={isSettingsOpen}>
      <BiosenseSignalMonitor
        showMonitor={!(isMobile && isSettingsOpen)}
        cameraId={cameraId}
        onLicenseStatus={updateLicenseStatus}
        onSettingsClick={toggleSettingsClick}
        isSettingsOpen={isSettingsOpen}
      />
      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />
    </Container>
  );
};

export default App; 